import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { toast } from "sonner";
import { Loader2, Save, FileText, ChevronRight, Maximize2, X } from "lucide-react";

// Structura secțiunii de rapoarte
const SECTIONS = [
  { key: "pm", label: "Raport Project Management", children: null },
  { key: "arh", label: "Raport Arhitectură", doc: true, children: null },
  {
    key: "inst", label: "Raport Instalații", children: [
      { key: "instalatii_executie", label: "Raport Execuție", doc: true },
      { key: "instalatii_design", label: "Raport Design", doc: true },
      { key: "instalatii_intern", label: "Raport Intern", doc: true },
    ],
  },
];

export default function ReportsDocs() {
  const [active, setActive] = useState(null); // {key,label,doc}
  const [openGroups, setOpenGroups] = useState({ inst: true });
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const iframeRef = useRef(null);

  async function openDoc(node) {
    setActive(node);
    setFullscreen(false);
    if (!node.doc) { setHtml(""); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/staff/report-docs/${node.key}`);
      setHtml(data.html || "");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }

  // Randează raportul într-un iframe izolat (sandbox) — scripturile din raport
  // rulează într-o origine opacă, fără acces la sesiunea/cookie-urile aplicației.
  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.srcdoc = html || "<!doctype html><html><body></body></html>";
    }
  }, [html, active]);

  // ESC iese din fullscreen
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  async function save() {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const current = "<!DOCTYPE html>\n" + doc.documentElement.outerHTML;
    setSaving(true);
    try {
      await api.put(`/staff/report-docs/${active.key}`, { html: current });
      setHtml(current);
      toast.success("Raport salvat în portal");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  }

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1500px]">
        <div className="mb-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand mb-2">Rapoarte</p>
          <h1 className="font-display font-semibold text-[38px] tracking-[-0.02em] leading-tight text-ink">Rapoarte proiect</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
          {/* Nav */}
          <div className="bg-white border border-neutral-200 p-2 h-max">
            {SECTIONS.map((s) => (
              <div key={s.key}>
                {s.children ? (
                  <>
                    <button onClick={() => setOpenGroups(g => ({ ...g, [s.key]: !g[s.key] }))}
                      data-testid={`nav-${s.key}`}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-ink hover:bg-neutral-50 rounded-lg">
                      <ChevronRight className={`h-4 w-4 transition-transform ${openGroups[s.key] ? "rotate-90" : ""}`} />
                      <FileText className="h-4 w-4 text-brand" /> {s.label}
                    </button>
                    {openGroups[s.key] && (
                      <div className="ml-6 border-l border-neutral-200 pl-2">
                        {s.children.map((c) => (
                          <button key={c.key} onClick={() => openDoc(c)}
                            data-testid={`nav-${c.key}`}
                            className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                              active?.key === c.key ? "bg-brand/10 text-brand font-medium" : "text-ink-muted hover:bg-neutral-50"
                            }`}>
                            {c.label}{c.doc ? "" : " ·"}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <button onClick={() => openDoc(s)}
                    data-testid={`nav-${s.key}`}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      active?.key === s.key ? "bg-brand/10 text-brand" : "text-ink hover:bg-neutral-50"
                    }`}>
                    <FileText className="h-4 w-4 text-brand" /> {s.label}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Content */}
          <div className="min-h-[600px]">
            {!active ? (
              <div className="flex items-center justify-center h-[600px] border border-dashed border-neutral-200 rounded-xl text-ink-muted" data-testid="reports-placeholder">
                Selectează un raport din meniul din stânga.
              </div>
            ) : !active.doc ? (
              <div className="flex items-center justify-center h-[600px] border border-dashed border-neutral-200 rounded-xl text-ink-muted">
                „{active.label}" — în curând.
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center h-[600px] text-ink-muted"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Se încarcă...</div>
            ) : (
              <div className={fullscreen ? "fixed inset-0 z-[100] bg-white flex flex-col" : "bg-white border border-neutral-200"}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 shrink-0">
                  <div className="font-display font-semibold text-ink">{active.label}</div>
                  <div className="flex items-center gap-2">
                    {!fullscreen ? (
                      <button onClick={() => setFullscreen(true)} data-testid="report-open-fullscreen"
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-ink text-white text-sm rounded hover:bg-brand transition-colors">
                        <Maximize2 className="h-4 w-4" /> Editează
                      </button>
                    ) : (
                      <>
                        <button onClick={save} disabled={saving} data-testid="report-save"
                          className="inline-flex items-center gap-1.5 px-4 py-2 border border-neutral-200 text-ink text-sm rounded hover:border-brand hover:text-brand transition-colors disabled:opacity-60">
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvează în portal
                        </button>
                        <button onClick={() => setFullscreen(false)} data-testid="report-close-fullscreen"
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-ink text-white text-sm rounded hover:bg-brand transition-colors">
                          <X className="h-4 w-4" /> Închide
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <iframe ref={iframeRef} title="raport" data-testid="report-iframe"
                  sandbox="allow-scripts allow-popups allow-forms"
                  className={fullscreen ? "w-full flex-1" : "w-full"}
                  style={fullscreen ? { border: "none" } : { height: "75vh", border: "none" }} />
              </div>
            )}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
